import { Component, OnInit, ChangeDetectorRef, NgZone, HostListener, OnDestroy, Input, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { initiateRemixer } from '../../assets/build-remix/super-remix8773.js'
import { ActivatedRoute, Router } from '@angular/router';
import { Data, SongService, ProfileService, SingService, RecordingService, UtilService } from '@app/core';
import { ToastrService } from 'ngx-toastr';
import Bugsnag from '@bugsnag/js';
import { RecorderStore } from '../superrecorder/recorder/state/recorder.store';
import { ThemeService, liftToLight } from '../superrecorder/recorder/services/theme.service';

@Component({
  selector: 'app-superremixer',
  templateUrl: './superremixer.component.html',
  styleUrls: ['./superremixer.component.scss']
})
export class SuperremixerComponent implements OnInit, OnDestroy {
  @HostListener('window:keydown', ['$event'])
  keyEvent(e: KeyboardEvent) {
    if(e.keyCode == 32) {
      if(this.loaded = true && !this.uploaded) {
        switch(this.UIState) {
          case 'review':
            this.pause()
            break
          case 'reviewPaused':
            this.play()
            break
        }
        e.stopImmediatePropagation();
        e.preventDefault();
      }


    }

  }
  @ViewChild('recVideo') private recVideo: ElementRef;
  @ViewChild('duetVideo') private duetVideo: ElementRef;
  @ViewChild('topUI') private topUI: ElementRef;
  @ViewChild('bottomUI') private bottomUI: ElementRef;
  @ViewChild('countdown') private countdown: ElementRef;
  @ViewChild('errorMessage') private errorMessage: ElementRef;
  @ViewChild('errorMessageContainer') private errorMessageContainer: ElementRef;
  @ViewChild('lyricsCanvas') private canvas: ElementRef;
  id;
  wasmPath = '/assets/superpowered.wasm';
  workletPath = '/assets/scripts/workletProcessor.js';

  blueLimitReached = false;
  pitchDisabled = false;
  remixer;
  loaded = false;
  duration = 0;
  playhead = 0;
  interval = null;
  uuid;
  UIState = 'reviewPaused'
  containerOpen = false;
  sources = {}
  videoDropDown = false;
  micDropDown = false;
  finished = false;
  scrubberOn = false;
  volumeBar = false;
  pitchBar = false;
  syncBar = false;
  lyricBar = false;
  lyricSpeed = 0;
  duet = false;
  uploaded = false
  sync = 0;
  mmsb = true;
  pitch = 0
  micVolume = 1;
  count = 5;
  musicVolume = 1;
  showSyncModal = false;
  lyricSize = 0;
  lyricsVisible = true;
  params;
  part;
  jam = false
  remix;
  urlChanges = false;
  version = '7.0'
  UI = {
    volumeButton: true,
    lyricButton: false,
    syncButton: true,
    mmsbButton: true,
    saveOrTryAgainButtons: true,
    pauseButton: false,
    resumePracticeButton: true
  }


  constructor(    
    private activatedRoute: ActivatedRoute,
    private utilService: UtilService,
    private singService: SingService,
    private remixService: SongService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private recordingService: RecordingService,
    private profileService: ProfileService,
    private data: Data,
    private router: Router,
    public recorderStore: RecorderStore,
    private themeService: ThemeService,) {

  }

  get micVolumeDisplay(): number {
    return Math.round(this.micVolume * 50);
  }

  get musicVolumeDisplay(): number {
    return Math.round(this.musicVolume * 50);
  }

  onMicVolumeSliderChange(v: number): void {
    const value = v;
    this.micVolume = value;
    if (this.remixer) this.remixer.changeVocalVolume(value);
    this.detectChanges();
  }

  onMusicVolumeSliderChange(v: number): void {
    const value = v;
    this.musicVolume = value;
    if (this.remixer) this.remixer.changeMusicVolume(value);
    this.detectChanges();
  }

  onSyncSliderChange(v: number): void {
    this.onSyncChange(v);
  }

  // Live pill/fill update while dragging; the real apply happens on release (change).
  onSyncSliderInput(v: number): void {
    this.sync = v;
    this.detectChanges();
  }

  // ── Sync pill click-to-edit (mirrors the recorder) ─────────────────────────
  syncEditing = false;
  syncDraft: number | null = null;
  @ViewChild('syncInput') private syncInput?: ElementRef<HTMLInputElement>;

  startSyncEdit(): void {
    this.syncDraft = this.sync;
    this.syncEditing = true;
    requestAnimationFrame(() => {
      const el = this.syncInput?.nativeElement;
      if (el) { el.focus(); el.select(); }
    });
  }

  commitSyncEdit(raw: string | number): void {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!isNaN(n)) {
      this.onSyncChange(Math.max(-500, Math.min(500, n)));
    }
    this.syncEditing = false;
  }

  cancelSyncEdit(): void {
    this.syncEditing = false;
  }

  // ── Generic value-pill click-to-edit ───────────────────────────────────────
  // Click a volume pill to type an exact 0–100 value. Mirrors the sync pill and
  // the recorder's pill-edit mechanism. (Sync keeps its own handlers above.)
  editingPill: string | null = null;
  pillDraft: number | null = null;
  @ViewChildren('pillInput') private pillInputs?: QueryList<ElementRef<HTMLInputElement>>;

  startPillEdit(name: string, current: number): void {
    this.pillDraft = current;
    this.editingPill = name;
    requestAnimationFrame(() => {
      const inputs = this.pillInputs?.toArray() ?? [];
      const el = inputs.find(e => e.nativeElement.offsetParent !== null)?.nativeElement
               ?? inputs[0]?.nativeElement;
      if (el) { el.focus(); el.select(); }
    });
  }

  commitPillEdit(name: string, raw: string | number, min: number, max: number): void {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!isNaN(n)) {
      const clamped = Math.max(min, Math.min(max, n));
      switch (name) {
        case 'music': this.onMusicVolumeSliderChange(clamped / 50); break; // 0–100 display → 0–2 gain
        case 'vocal': this.onMicVolumeSliderChange(clamped / 50); break;
      }
    }
    this.editingPill = null;
  }

  cancelPillEdit(): void {
    this.editingPill = null;
  }

  onSyncTestClick(): void {
    if (this.remixer?.superPlayer?.playing) this.pause();
    this.showSyncModal = true;
    this.detectChanges();
  }

  onSyncModalApplied(value: number): void {
    this.onSyncChange(value);
  }

  onSyncModalClosed(): void {
    this.showSyncModal = false;
    this.detectChanges();
  }

  onLyricSizeChange(v: number): void {
    this.lyricSize = Math.round(v);
    const scale = 1 + this.lyricSize * 0.15;
    document.documentElement.style.setProperty('--lyric-scale', String(scale));
    localStorage.setItem('ssLyricSize', String(this.lyricSize));
    this.detectChanges();
  }

  toggleLyrics(): void {
    this.lyricsVisible = !this.lyricsVisible;
    this.detectChanges();
  }

  sliderBg(value: number, min: number, max: number): string {
    const pct = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, #58c7c9 ${pct}%, rgba(232,236,243,0.12) ${pct}%)`;
  }

  get storeFillLightStyle(): Record<string, string> {
    const fl = this.recorderStore.fillLight();
    const bFactor = fl.brightness / 100;
    let r: number, g: number, b: number;

    if (!fl.toneOn) {
      const themed = this.themeService.activeTheme.fillLight;
      if (themed) {
        [r, g, b] = themed.light;
      } else {
        const shellBg = this.themeService.activeTheme.tokens['--shell-bg'] ?? '';
        const hex = shellBg.match(/#[0-9a-fA-F]{6}/)?.[0] ?? '#04071c';
        [r, g, b] = liftToLight(hex);
      }
    } else {
      const t = fl.warmth / 100;
      const { cool, warm } = this.themeService.toneFor(this.themeService.activeTheme);
      const [cR, cG, cB] = cool;
      const [wR, wG, wB] = warm;
      if (t <= 0.5) {
        const f = t * 2;
        r = Math.round(cR + (255 - cR) * f);
        g = Math.round(cG + (255 - cG) * f);
        b = Math.round(cB + (255 - cB) * f);
      } else {
        const f = (t - 0.5) * 2;
        r = Math.round(255 + (wR - 255) * f);
        g = Math.round(255 + (wG - 255) * f);
        b = Math.round(255 + (wB - 255) * f);
      }
    }

    return {
      'box-shadow': `inset 0 0 0 2000px rgba(${r}, ${g}, ${b}, ${bFactor})`,
      '--fill-brightness': String(bFactor),
    };
  }

  ngOnInit() {
    const savedLyricSize = parseInt(localStorage.getItem('ssLyricSize') ?? '0', 10);
    if (!isNaN(savedLyricSize)) {
      this.lyricSize = savedLyricSize;
      document.documentElement.style.setProperty('--lyric-scale', String(1 + savedLyricSize * 0.15));
    }
    this.activatedRoute.params.subscribe(params => {
      this.id = parseInt(params.id)
      let userAgent = window.navigator.userAgent.toLowerCase()
      // if (userAgent.indexOf('firefox',0) !== -1) {
      //   this.router.navigate(['/d/remix/' + this.id]) //go to old remixer if it's firefox
      //   return;
      // }
      this.loadRemixData()
    });
  }
  async loadRemixData() {
    try {
      const requests = [];
      requests.push(this.recordingService.getRemixData(this.id).toPromise());
      let results = await Promise.all(requests);
      this.remix = results[0];
      if(typeof this.remix.parent_uri != 'undefined') {
        if(this.remix.parent_uri != null) {
          this.jam = true;
          this.duetVideo.nativeElement.style.removeProperty('display')
          //this.router.navigate(['/d/remix/' + this.id])
        }
      }
      if(this.remix.users.length > 1) {
        this.duet = true;
      }

      this.mmsb = this.remix.mmsb;
      this.micVolume = this.remix.meta.mic_vol
      this.musicVolume = this.remix.meta.base_vol
      this.sync = parseInt(this.remix.meta.latency);

      // Apply saved lighting from the recording's meta to the global
      // RecorderStore so the brightness slider and overlay reflect the
      // values captured during recording. Falls back to the store's
      // existing values when the meta keys are absent (older recordings).
      const savedBrightness = this.remix.meta.brightness;
      const savedToneOn     = this.remix.meta.tone_on;
      const savedWarmth     = this.remix.meta.warmth;
      if (typeof savedBrightness === 'number') this.recorderStore.setBrightness(savedBrightness);
      if (typeof savedToneOn === 'boolean')    this.recorderStore.setToneOn(savedToneOn);
      if (typeof savedWarmth === 'number')     this.recorderStore.setWarmth(savedWarmth);
      this.remixer = initiateRemixer(
        this.workletPath,
        this.wasmPath,
        this.recVideo.nativeElement,
        this.duetVideo.nativeElement,
        this.remix,
        //this.canvas.nativeElement,
        this.jam
      );
      this.hookUpEvents()
      this.remixer.init()
    } catch (err) {
      this.errorMessageContainer.nativeElement.style.display = 'flex'
      this.errorMessage.nativeElement.innerText = err
      this.UIState = 'noPermissions'
      this.changeUIState()
      let errMsg = err.toString();
      Bugsnag.notify('Remixer loadRemixData error : ' + errMsg);
    }
  }
  detectChanges() {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  hookUpEvents(){
    //remix is loaded!
    this.remixer.addEventListener('initDom', ()=>this.initDom())
    this.remixer.addEventListener('playheadUpdate', ()=>this.playheadUpdate()) // playhead is in a new spot, update the scrubber
    this.remixer.addEventListener('show_message', (event, error)=>{
      this.toastr.info(error)
      this.detectChanges()
      Bugsnag.notify('Remixer hookUpEvents error: ' + error);
    })
    this.remixer.addEventListener('no_permissions', (event, error)=>{
      this.errorMessageContainer.nativeElement.style.display = 'flex'
      this.errorMessage.nativeElement.innerText = error
      this.UIState = 'noPermissions'
      this.changeUIState()
      Bugsnag.notify('Remixer hookUpEvents-no permissions: ' + error);
    })


    //this.remixer.addEventListener('startScrubber', ()=>this.startScrubber())
    this.remixer.addEventListener('durationUpdate', ()=>this.durationUpdate())

  }

  initDom() {
    this.duration = this.remixer.superPlayer.duration
    this.loaded = true;
    if(this.mmsb){
      this.remixer.superPlayer.mmsbOn()
    } else {
      this.remixer.superPlayer.mmsbOff()
    }
    this.remixer.changeVocalVolume(this.micVolume);
    this.remixer.changeMusicVolume(this.musicVolume);
    this.onSyncChange(this.sync);
    this.detectChanges()
    if(this.jam) {
      this.changeDuetStyles()
    }
  }
  changeDuetStyles(){
    // NOTE: #lyricsCanvas / #topUI / #bottomUI no longer exist in the remixer
    // shell (removed in the refactor). The old code here set styles on their
    // nativeElements and threw "undefined nativeElement", which aborted duet
    // setup and left only one video visible. Side-by-side layout is now handled
    // by the `.is-duet` CSS on #actualVideoContainer; here we only clear the
    // duet video's poster, guarded.
    if (this.remix?.webcam && this.duetVideo?.nativeElement) {
      this.duetVideo.nativeElement.poster = '';
    }
    this.detectChanges();
  }
  backClicked(){
    window.history.back();
  }
  playheadUpdate(){
    if(!this.scrubberOn) {
      return
    }
    if(!this.remixer?.superPlayer) {
      return
    }
    this.playhead = this.remixer.superPlayer.playhead;
    if(this.playhead >= (this.duration - 500)) {
        this.pause()
        this.remixer.seek(0)
        this.playhead = 0
    }
    this.detectChanges()
  }
  msToTime(s) {
    let pad = (n, z = 2) => ('00' + n).slice(-z);
    return pad((s%3.6e6)/6e4 | 0) + ':' + pad((s%6e4)/1000|0);
  }
  startScrubber(){

    this.scrubberOn = true;
      // if(typeof this.interval != 'undefined') {
      //   this.stopScrubber()
      // }
      this.interval = setInterval(()=>{
        this.remixer?.superPlayer?.getPosition();
      }, 100);
  }
  stopScrubber(){
      this.scrubberOn = false;
      clearInterval(this.interval);
      this.interval = null;
  }
  changeRangeTime(e) {
    if(this.scrubberOn) {
      this.stopScrubber()

    }
    this.playhead = parseFloat(e)
    this.detectChanges()
}
  seek(e) {
    if(this.UIState == 'review') {
      this.pause();
    }
    e = parseFloat(e)
    this.remixer.seek(e)
    this.playhead = e
    if(this.remixer.superPlayer.playing && !this.scrubberOn) {
      this.startScrubber()
    }
    this.detectChanges()
  }
    pause(){
      this.remixer.pause()
      this.UIState = 'reviewPaused'
      this.stopScrubber();
      this.changeUIState()
    }
    play(){
      this.remixer.play()
      this.UIState = 'review'
      this.changeUIState()
      this.startScrubber()
  }
  durationUpdate() {
    this.duration = this.remixer.superPlayer.duration
    this.seek(0)
    this.playhead = 0
    let sync = parseInt(localStorage.getItem("sync"))
    if (sync) {
      this.sync = sync
      this.remixer.sync(this.sync);
    }
    this.toastr.success('Review loaded!')
    this.detectChanges()
  }
  onSyncClick() {
    this.containerOpen = true;
    this.syncBar = true;
    this.detectChanges()
  }
  onSyncChange(e) {
    this.sync = e
    if(this.remixer.superPlayer.playing) {
      this.pause()
      this.UIState = 'reviewPaused'
      this.changeUIState()
    }
    this.remixer.sync(e)
    this.detectChanges()
  }

  // ── Sync nudge buttons (± beside the slider) ───────────────────────────────
  // Click a button = ±1 ms; press and hold = repeats by ±5 ms. Clamped -500..500.
  private syncHoldTimer: any = null;
  private syncHoldInterval: any = null;

  nudgeSync(delta: number): void {
    const next = Math.max(-500, Math.min(500, this.sync + delta));
    if (next !== this.sync) {
      this.onSyncChange(next);
    }
  }

  startSyncNudge(delta: number): void {
    this.nudgeSync(delta);            // immediate single step (±1)
    this.stopSyncNudge();
    const dir = delta < 0 ? -1 : 1;
    this.syncHoldTimer = setTimeout(() => {
      this.syncHoldInterval = setInterval(() => this.nudgeSync(5 * dir), 120);
    }, 400);
  }

  stopSyncNudge(): void {
    if (this.syncHoldTimer) { clearTimeout(this.syncHoldTimer); this.syncHoldTimer = null; }
    if (this.syncHoldInterval) { clearInterval(this.syncHoldInterval); this.syncHoldInterval = null; }
  }
  onSyncClose() {
    this.containerOpen = false;
    this.syncBar = false;
    this.detectChanges()
  }
  onVolumeClick() {
    this.containerOpen = true;
    this.volumeBar = true;
    this.detectChanges()
  }
  onMicVolumeChange(e) {
    let value = parseFloat(e)
    this.micVolume = value;
    this.remixer.changeVocalVolume(value)
  }
  onMusicVolumeChange(e) {
    let value = parseFloat(e)
    this.musicVolume = value;
    this.remixer.changeMusicVolume(value)
  }
  onVolumeClose() {
    this.containerOpen = false;
    this.volumeBar = false;
    this.detectChanges()
  }
  onLyricSpeedClick() {
    this.containerOpen = true;
    this.lyricBar = true;
    this.detectChanges()
  }
  onLyricSpeedChange(e) {
    let value =  Math.round((e * 0.6) * 10) / 10 //rounds to one digit place
    if(this.remixer.superPlayer.playing) {
      this.pause();
      if(this.remixer.mode == 'review') {
        this.UIState = 'reviewPaused'
      } else if (this.UIState != 'default'){
        this.UIState = 'practicePaused'
      }
      this.changeUIState()
    }
    this.lyricSpeed = value / 0.6;
    this.remixer.lyricsPlayer.changeSpeed(value)
  }
  onLyricSpeedClose() {
    this.lyricBar = false;
    this.containerOpen = false;
    this.detectChanges()
  }

  onChangeMMSB() {
    if(this.mmsb) {
      this.remixer.superPlayer.mmsbOn()
    } else {
      this.remixer.superPlayer.mmsbOff()
    }
    //this.mmsb = !this.mmsb

    this.detectChanges()
  }
  async upload() {
    if(this.uploaded) {
      return
    }
    const fl = this.recorderStore.fillLight();
    let payload = {
      meta: {
        latency: this.sync,
        mic_volume: this.micVolume,
        base_volume: this.musicVolume,
        brightness: fl.brightness,
        tone_on:    fl.toneOn,
        warmth:     fl.warmth,
      },
      mmsb: this.mmsb,
      webcam: this.remix.webcam
    }
    try {
      await this.recordingService.saveRemix(this.id, payload).toPromise();
      this.data.storage = { recordingId: this.id };
      this.uploaded = true;
      this.router.navigate(['/d/remix/success']);
    } catch (err) {
      let errMsg = err.toString();
      Bugsnag.notify('Remixer  upload error: ' + errMsg);
    } 
  }
  ngOnDestroy() {
    this.stopScrubber();
    this.stopSyncNudge();
    if(typeof this.remixer != 'undefined') {
      this.pause()
      this.remixer = null;
      if(!this.uploaded && this.loaded) {
        window.location.reload();
      }
    }

  }

changeUIState() {
  //behold, the grand switch statement!
  switch(this.UIState) {
    case 'noPermissions':
      this.UI.volumeButton = false;
      this.UI.lyricButton = false;
      this.UI.syncButton = false;
      this.UI.mmsbButton = false;
      this.UI.saveOrTryAgainButtons = false;
      this.UI.pauseButton = false;
      this.UI.resumePracticeButton = false;
      break;
    case 'review':
      this.UI.volumeButton = true;
      this.UI.lyricButton = false;
      this.UI.syncButton = true;
      this.UI.mmsbButton = true;
      this.UI.saveOrTryAgainButtons = true;
      this.UI.pauseButton = true;
      this.UI.resumePracticeButton = false;
      break;
    case 'reviewPaused':
      this.UI.volumeButton = true;
      this.UI.lyricButton = false;
      this.UI.syncButton = true;
      this.UI.mmsbButton = true;
      this.UI.saveOrTryAgainButtons = true;
      this.UI.pauseButton = false;
      this.UI.resumePracticeButton = true;
      break;
  }
  
  this.detectChanges();
}
}









